// marketplace-api.ts — typed client for the capability-marketplace REST API
// (marketplace.md §4). These endpoints are manager-native (NOT proxied to the
// agent container) and now live at the manager root — identity comes from the
// Auth0 bearer (auth.md), not a `/users/:user` URL segment — so we reuse the
// same base-url + auth-header helpers as lib/api.ts.
//
// DTO shapes (CatalogEntry / ConnectionView) mirror marketplace.md §4 verbatim
// — other components code against the same contract, so divergence here breaks
// integration.

import { API_ORIGIN, jsonOrThrow, authHeaders } from './api';

// JSON-bodied requests merge the bearer with the content-type header.
async function jsonHeaders(): Promise<Record<string, string>> {
	return { ...(await authHeaders()), 'content-type': 'application/json' };
}

// ── DTOs (marketplace.md §4) ─────────────────────────────────────────────────

export type CapabilityKind = 'mcp' | 'skill';
export type CapabilityTier = 'curated' | 'personal' | 'community';
// Three-tier (migration 005): 'restricted' = installable only by admins + users
// explicitly granted via the admin "MCP access" panel.
export type CapabilityVisibility = 'everyone' | 'admin' | 'restricted';
// 'vault_bearer' is the personal-mcp case (§12): a user-supplied bearer vaulted
// per-user, resolved into the loadout without refresh; never part of a shared spec.
export type CapabilityAuth = 'none' | 'shared_bearer' | 'oauth' | 'vault_bearer';
export type CapabilityHealth = 'connected' | 'failed' | 'needs_auth' | 'unknown';
// Personal-cap share lifecycle (§12): a personal cap starts 'none', goes
// 'pending' on request, then an admin flips it 'approved' (promote) or 'rejected'.
export type ShareStatus = 'none' | 'pending' | 'approved' | 'rejected';

// Per-user credential provisioning help (migration 003) — where/how to get a
// token for caps that need the user's own credential (auth oauth/vault_bearer).
export type Provisioning = {
	helpUrl?: string;
	instructions?: string;
	scopes?: string[];
};

export type CatalogEntry = {
	id: string;
	kind: CapabilityKind;
	name: string;
	summary: string;
	description: string;
	tier: CapabilityTier;
	visibility: CapabilityVisibility;
	publisher: string;
	icon: string | null;
	latestVersion: string;
	installed: boolean;
	enabled: boolean;
	pinned: boolean;
	// Admin availability (migration 005). active = the global on/off; installable =
	// the server's EFFECTIVE canInstall for THIS user (active + visibility + grants);
	// unavailableReason explains an installed card the user can no longer use
	// ('admin_off' = an admin turned it off / removed access) so the UI shows an
	// honest "Turned off by your admin" state instead of a silently-dead On card.
	active: boolean;
	installable: boolean;
	unavailableReason?: 'admin_off';
	// Write classification + provisioning help (migration 003). access is the
	// honest read/write badge; provisioning is the "where do I get a token" bundle.
	access: 'read_only' | 'read_write';
	provisioning: Provisioning;
	// default-on starter flag (migration 004): pre-checked in onboarding + badged.
	recommended: boolean;
	// UI grouping bucket (migration 014): null ⇒ flat; a named group (e.g. 'otto')
	// collapses its members into one collapsible group block in the Skills room.
	group: string | null;
	// Companion skills (migration 015): skill ids the agent reads before using this
	// cap's tools. Non-empty on MCP twins that carry a product skill; drives the
	// "Used automatically by …" pointer on the skill's detail sheet.
	companionSkills: string[];
	// personal caps (§12): owner is the user_id that authored it (null for shared);
	// shareStatus tracks the promote lifecycle.
	owner: string | null;
	shareStatus: ShareStatus;
	// mcp-only:
	auth?: CapabilityAuth;
	health?: CapabilityHealth;
	requiresConnection?: boolean; // auth in {oauth,vault_bearer}: needs connect/token first
	connected?: boolean; // requiresConnection && a connected connection exists
	// The REAL, broker-verified auth state for a requiresConnection cap (mirrors the
	// manager's marketplace-protocol.ts). Unlike `connected` — which is the group-
	// aware "does SOME sibling hold a token" view that can read true for a Google cap
	// whose own grant is dead — authState reflects whether THIS cap's credential is
	// actually live: 'authed' = a working grant, 'needs_auth' = the grant exists but
	// is dead (the 401 `connected` can't see), 'not_connected' = no credential yet.
	// Omitted for non-requiresConnection caps, and undefined in pre-authState payloads
	// (the helpers fall back to the connected-based behavior when it's absent).
	authState?: 'authed' | 'needs_auth' | 'not_connected';
};

export type ConnectionStatus = 'pending' | 'connected' | 'needs_auth' | 'error';

export type ConnectionView = {
	capabilityId: string;
	name: string;
	status: ConnectionStatus;
	updatedAt: number;
};

// `onboarded` is the mandatory first-run gate (roadmap.md #4). False until the
// user finishes the welcome wizard; never returns to false (no re-onboard flow).
// `defaultProvider` = the admin's "Default provider for new chats" (drives which
// models the new-chat picker lists). OPTIONAL on the wire: an OLD manager (deploy
// skew — separate Coolify apps) omits it; readers default to 'openai'.
export type Me = {
	user: string;
	isAdmin: boolean;
	// Super admin (manager migration 029): the role formerly labelled "Config".
	// Optional on the wire (an old manager omits it); readers default to false.
	isSuperAdmin?: boolean;
	onboarded: boolean;
	defaultProvider?: 'claude' | 'openai';
	// The model list for THIS user's agent container release (manager migration
	// 027). Optional/null on the wire: an old manager omits it and a docker/DB blip
	// nulls it — readers fall back to the static curated lists.
	models?: import('./agent-releases-api').UserModelCatalog | null;
};

// ── Admin hub (migrations 005 + 006) ─────────────────────────────────────────
// Admin-only management view of a capability + the grant/people roster.
// 'db' = a company token is set in the vault (wins), 'env' = falling back to the
// compose-literal default, 'missing' = neither, 'na' = this cap's auth mode needs
// no shared token. NEVER the token value itself.
export type SharedTokenStatus = 'db' | 'env' | 'missing' | 'na';

export type AdminCapability = {
	id: string;
	latestVersion: string | null;
	kind: CapabilityKind;
	name: string;
	summary: string;
	// Editable catalog copy (migration 022) — effective values (admin override wins
	// over the seed). `overridden` = fields currently carrying an override (badge +
	// per-field reset via setCapabilityOverrides({ field: null })).
	description: string;
	icon: string | null;
	group: string | null;
	companionSkills: string[];
	overridden: string[];
	tier: CapabilityTier;
	visibility: CapabilityVisibility;
	active: boolean;
	installCount: number;
	grants: string[]; // granted user_ids (meaningful when visibility==='restricted')
	// Default-on starter flag (migration 004), now admin-editable from the hub.
	recommended: boolean;
	defaultInstalled: boolean;
	canDefaultInstall: boolean;
	// Company-wide shared token (migration 006), mcp-only. tokenEnv is the env-var
	// NAME (never the secret); read/write twins share one tokenEnv. tokenMode tells
	// the UI the paste shape; sharedTokenStatus drives the status pill.
	tokenEnv?: string | null;
	tokenMode?: 'bearer' | 'basic';
	sharedTokenStatus?: SharedTokenStatus;
};

export type AdminUser = {
	userId: string;
	label: string; // resolved name/email, or the raw id when Auth0 resolution is unavailable
	email: string | null;
	onboarded: boolean;
	// Admin status (migration 006). isAdmin = env floor ∪ DB set; isFloorAdmin =
	// configured in MARKETPLACE_ADMINS (un-removable — the toggle is locked).
	isAdmin: boolean;
	isFloorAdmin: boolean;
	// Super admin (migration 029). Only true when the VIEWER is a super admin; to
	// everyone else a super admin reads as a locked "Config" admin.
	isSuperAdmin?: boolean;
	isConfigSuperAdmin?: boolean;
};

// ── Super admin (migration 029) ─────────────────────────────────────────────
export type AdminPersonalCapability = {
	id: string;
	kind: CapabilityKind;
	name: string;
	summary: string;
	owner: string;
	ownerLabel: string;
	shareStatus: ShareStatus;
	active: boolean;
	installCount: number;
};

export type AdminCapabilityInstaller = { userId: string; label: string; enabled: boolean };

export type OffboardPreview = {
	userId: string;
	label: string;
	email: string | null;
	isAdmin: boolean;
	isSuperAdmin: boolean;
	isConfigSuperAdmin: boolean;
	personalCapabilities: AdminPersonalCapability[];
	reports: { id: string; title: string; visibility: 'private' | 'team'; isLive: boolean; updatedAt: string }[];
	scheduledChats: { id: string; name: string; enabled: boolean }[];
	installCount: number;
	connectionCount: number;
	sessionCount: number;
	hasDataDir: boolean;
};

export type OffboardResult = {
	userId: string;
	publishedCapabilities: string[];
	removedCapabilities: string[];
	sharedReports: string[];
	deletedReports: string[];
	purged: Record<string, number>;
	containerRemoved: boolean;
	archivePath: string | null;
};

export type OffboardedUser = {
	userId: string;
	label: string;
	email: string | null;
	offboardedBy: string | null;
	offboardedAt: string;
	archivePath: string | null;
};

// One row in the per-user installs sub-panel (admin hub): force add/remove for
// on/offboarding.
export type AdminUserInstall = {
	capabilityId: string;
	name: string;
	kind: CapabilityKind;
	enabled: boolean;
};

// Admin token-usage report. Credits are Codex subscription usage calculated at
// OpenAI's published standard-tier rates; apiEquivalentUsd is a hypothetical
// current API-key comparison; costUsd is real billed cost only.
export type AdminUsageRow = {
	sub: string;
	name: string | null;
	email: string | null;
	inputTokens: number;
	outputTokens: number;
	cacheReadTokens: number;
	cacheCreationTokens: number;
	totalTokens: number;
	credits: number;
	apiEquivalentUsd: number;
	costUsd: number;
	turns: number;
	sessions: number;
};
// One UTC-day bucket of usage summed across all users — the daily series the admin
// Usage chart plots (gap-filled to a continuous axis for bounded windows). Every
// field is additive so the chart can roll daily → weekly → monthly by summing.
export type AdminUsageDay = {
	day: number; // bucket-start epoch ms (UTC midnight daily, top of the hour hourly)
	inputTokens: number;
	outputTokens: number;
	cacheReadTokens: number;
	cacheCreationTokens: number;
	totalTokens: number;
	credits: number;
	apiEquivalentUsd: number;
	costUsd: number;
	turns: number;
};
export type AdminUsageReport = {
	from: number;
	to: number;
	users: AdminUsageRow[];
	totals: Omit<AdminUsageRow, 'sub' | 'name' | 'email'>;
	series: AdminUsageDay[];
	coverage: { turnsWithCredits: number; turnsWithApiEquivalent: number; turnsWithCost: number; turns: number };
};

// ── Admin sessions report (per-chat token/cost/MCP rollup) ───────────────────
// Mirrors the manager's marketplace-protocol.ts verbatim. One row per chat over
// a time window, with the per-MCP byte-estimate breakdown the manager derives
// from the host FS. `estTokens`/`hasBytes` are honest about coverage: a turn
// predating tool-I/O byte capture has hasBytes=false and renders a "—" rather
// than a misleading 0. costUsd is the real billed figure (Claude); MCP est
// tokens are byte-based estimates of tool I/O injected into context, NOT the
// model's billed per-MCP split.
export type AdminSessionMcp = { capId: string; calls: number; fails: number; estTokens: number; hasBytes: boolean };
export type AdminSessionRow = {
	owner: string; ownerName: string | null; ownerEmail: string | null;
	id: string; title: string | null;
	provider: 'claude' | 'codex'; model: string | null; promptVersion: string | null;
	effort: string | null; turns: number;
	inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheCreationTokens: number;
	totalTokens: number; credits: number; apiEquivalentUsd: number; costUsd: number; createdAt: number; lastActivity: number;
	mcps: AdminSessionMcp[];
};
export type AdminSessionsReport = { from: number; to: number; sessions: AdminSessionRow[]; coverage: { turnsWithBytes: number; turns: number } };
export type AdminSessionTurn = { at: number; effort: string | null; inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheCreationTokens: number; credits: number; apiEquivalentUsd: number; costUsd: number; toolCount: number; hasBytes: boolean };
export type AdminSessionDetail = { owner: string; id: string; title: string | null; provider: 'claude' | 'codex'; model: string | null; createdAt: number; turns: AdminSessionTurn[]; mcps: AdminSessionMcp[] };

// A pending share request, with the detail an admin needs to review before
// promoting (§12). The mcp variant is the spec sans any secret — a vaulted
// personal bearer never travels here (on promote it becomes shared_bearer with
// an admin-supplied company token). probe = the registration-time Streamable-
// HTTP handshake result (agent-registered MCPs), so the admin sees what tools
// the server actually exposed.
export type ShareReview = {
	id: string;
	kind: CapabilityKind;
	name: string;
	summary: string;
	description: string;
	owner: string; // raw Auth0 sub (fallback / dedupe key)
	ownerName: string | null; // resolved display name/email; null when unresolvable
	skillBody?: string; // skill: the SKILL.md body, for review
	mcp?: {
		transport: string;
		url: string;
		auth: string;
		probe?: { ok: boolean; toolNames: string[]; at: number } | null;
	};
};

// Author-a-capability payload (§12). Kind-agnostic envelope; the body diverges
// only at the two seams: a skill carries a SKILL.md body, an mcp a url + optional
// bearer (vaulted server-side — never stored in the spec or env).
export type CreateCapabilityInput = {
	kind: CapabilityKind;
	name: string;
	summary: string;
	description: string;
	skill?: { body: string };
	mcp?: { transport?: string; url: string; token?: string };
};

// ── endpoints (marketplace.md §4) ─────────────────────────────────────────────
// jsonOrThrow is shared from lib/api.ts (same error-unwrap contract).

export async function getMe(): Promise<Me> {
	const res = await fetch(`${API_ORIGIN}/me`, { headers: await authHeaders() });
	return jsonOrThrow<Me>(res, 'getMe');
}

// Idempotent — flips mp_users.onboarded to true and returns the new /me shape so
// the wizard can refresh the auth-store snapshot from this single response.
export async function markOnboarded(): Promise<Me> {
	const res = await fetch(`${API_ORIGIN}/me/onboarded`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	return jsonOrThrow<Me>(res, 'markOnboarded');
}

// Admin-only (settings → "Reset onboarding"). Flips onboarded back to false so
// the first-run wizard runs again; returns the new /me shape so the layout gate
// re-shows the wizard from a single response. 403 for non-admins.
export async function resetOnboarding(): Promise<Me> {
	const res = await fetch(`${API_ORIGIN}/me/onboarded/reset`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	return jsonOrThrow<Me>(res, 'resetOnboarding');
}

export async function getCatalog(): Promise<CatalogEntry[]> {
	const res = await fetch(`${API_ORIGIN}/catalog`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ capabilities: CatalogEntry[] }>(res, 'getCatalog');
	return body.capabilities;
}

// GET /catalog/:id/skill — the resolved content of a skill capability
// (transparency view + Agent-Skills export). SKILL.md is raw (frontmatter
// intact); `folder` is the export folder name the zip should use. 404s for
// unknown/locked skills — callers gate on entry.installable before fetching.
export type SkillContent = {
	id: string;
	name: string;
	folder: string;
	files: Record<string, string>; // relPath → UTF-8; 'SKILL.md' always present
};

export async function getSkillContent(capabilityId: string): Promise<SkillContent> {
	const res = await fetch(`${API_ORIGIN}/catalog/${encodeURIComponent(capabilityId)}/skill`, {
		headers: await authHeaders()
	});
	return jsonOrThrow<SkillContent>(res, 'getSkillContent');
}

export async function installCapability(capabilityId: string): Promise<CatalogEntry> {
	const res = await fetch(`${API_ORIGIN}/installs`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify({ capabilityId }),
	});
	const body = await jsonOrThrow<{ entry: CatalogEntry }>(res, 'installCapability');
	return body.entry;
}

// POST /installs/:id/access { write } — seamless read↔write flip (A2). The
// manager installs the target twin, carries the sealed credential across, and
// uninstalls the source under one rebuild; returns the resulting CatalogEntry
// (the target twin). The :id can be either twin — setAccess resolves the pair.
export async function setCapabilityAccess(capabilityId: string, write: boolean): Promise<CatalogEntry> {
	const res = await fetch(`${API_ORIGIN}/installs/${capabilityId}/access`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify({ write }),
	});
	const body = await jsonOrThrow<{ entry: CatalogEntry }>(res, 'setCapabilityAccess');
	return body.entry;
}

export async function uninstallCapability(capabilityId: string): Promise<void> {
	const res = await fetch(`${API_ORIGIN}/installs/${capabilityId}`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	// 204 on success; tolerate 404 so an already-gone install resolves cleanly.
	if (!res.ok && res.status !== 404) throw new Error(`uninstallCapability: ${res.status}`);
}

export async function getConnections(): Promise<ConnectionView[]> {
	const res = await fetch(`${API_ORIGIN}/connections`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ connections: ConnectionView[] }>(res, 'getConnections');
	return body.connections;
}

// Loadout barrier: the manager rewrites control/loadout.json from the user's
// CURRENT installs/connections and responds only after the write lands. The
// in-chat connect card awaits this before handing "please continue" to the agent,
// so the retry turn is guaranteed to read a loadout that includes the capability
// the user just enabled/connected (the OAuth callback marks `connected` seconds
// before its own rebuild finishes — this closes that window). Bounded so a slow
// re-mint can't wedge the Continue click; the caller treats failure as best-effort.
export async function refreshLoadout(): Promise<void> {
	const res = await fetch(`${API_ORIGIN}/loadout/refresh`, {
		method: 'POST',
		headers: await authHeaders(),
		signal: AbortSignal.timeout(15_000),
	});
	if (!res.ok) throw new Error(`refreshLoadout: ${res.status}`);
}

// Begins the Path-1 OAuth dance; the caller navigates to authorizeUrl. The
// manager 302s back to FRONTEND_ORIGIN/marketplace after the callback exchange.
export async function connectCapability(capabilityId: string): Promise<{ authorizeUrl: string }> {
	const res = await fetch(`${API_ORIGIN}/connections`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify({ capabilityId }),
	});
	return jsonOrThrow<{ authorizeUrl: string }>(res, 'connectCapability');
}

// vault_bearer connect: seal a user-pasted token for a curated per-user-token
// capability (e.g. Asana). The manager vaults it + rebuilds the loadout; no
// redirect (unlike the OAuth path above).
export async function connectCapabilityWithToken(capabilityId: string, token: string): Promise<void> {
	const res = await fetch(`${API_ORIGIN}/connections`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify({ capabilityId, token }),
	});
	await jsonOrThrow<{ connected: boolean }>(res, 'connectCapabilityWithToken');
}

export async function disconnectCapability(capabilityId: string): Promise<void> {
	const res = await fetch(`${API_ORIGIN}/connections/${capabilityId}`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	if (!res.ok && res.status !== 404) throw new Error(`disconnectCapability: ${res.status}`);
}

// ── personal capabilities + share-to-company (marketplace.md §12) ─────────────

// Author a personal capability owned by the current user. Kind-agnostic except
// for the skill/mcp body the caller fills in (the manager handles vaulting any
// mcp token and writing the SKILL.md).
export async function createCapability(input: CreateCapabilityInput): Promise<CatalogEntry> {
	const res = await fetch(`${API_ORIGIN}/capabilities`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify(input),
	});
	const body = await jsonOrThrow<{ entry: CatalogEntry }>(res, 'createCapability');
	return body.entry;
}

// Owner-only; the manager rejects if the cap was already promoted (tier≠personal).
export async function deleteCapability(capabilityId: string): Promise<void> {
	const res = await fetch(`${API_ORIGIN}/capabilities/${capabilityId}`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	if (!res.ok && res.status !== 404) throw new Error(`deleteCapability: ${res.status}`);
}

// Owner-only; flips a personal cap's share_status to 'pending' for admin review.
export async function requestShare(capabilityId: string): Promise<CatalogEntry> {
	const res = await fetch(`${API_ORIGIN}/capabilities/${capabilityId}/share`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ entry: CatalogEntry }>(res, 'requestShare');
	return body.entry;
}

// Admin-only; the queue of pending share requests with review detail.
export async function listShares(): Promise<ShareReview[]> {
	const res = await fetch(`${API_ORIGIN}/shares`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ pending: ShareReview[] }>(res, 'listShares');
	return body.pending;
}

// Admin-only; promote: tier→curated, visibility→everyone, share_status→approved.
export async function approveShare(capabilityId: string): Promise<CatalogEntry> {
	const res = await fetch(`${API_ORIGIN}/shares/${capabilityId}/approve`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ entry: CatalogEntry }>(res, 'approveShare');
	return body.entry;
}

// Admin-only; share_status→rejected (the cap stays personal, owner can re-request).
export async function rejectShare(capabilityId: string): Promise<CatalogEntry> {
	const res = await fetch(`${API_ORIGIN}/shares/${capabilityId}/reject`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ entry: CatalogEntry }>(res, 'rejectShare');
	return body.entry;
}

// ── Admin "MCP access" client (migration 005) ────────────────────────────────
// All admin-only (the manager returns 403 for non-admins). The mutations return
// the updated AdminCapability so the panel can reconcile a single row.
export async function listAdminCapabilities(): Promise<AdminCapability[]> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ capabilities: AdminCapability[] }>(res, 'listAdminCapabilities');
	return body.capabilities;
}

export async function listAdminUsers(): Promise<AdminUser[]> {
	const res = await fetch(`${API_ORIGIN}/admin/users`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ users: AdminUser[] }>(res, 'listAdminUsers');
	return body.users;
}

// Per-user token-usage report over [fromMs, toMs). Both are epoch ms; pass
// fromMs=0 for "all time". `granularity` picks the series bucket size (default
// daily). Admin-only (manager returns 403 for non-admins).
export type UsageGranularity = 'day' | 'hour';
export async function getTokenUsage(fromMs: number, toMs: number, granularity: UsageGranularity = 'day'): Promise<AdminUsageReport> {
	const qs = new URLSearchParams({ from: String(Math.trunc(fromMs)), to: String(Math.trunc(toMs)), granularity });
	const res = await fetch(`${API_ORIGIN}/admin/usage?${qs}`, { headers: await authHeaders() });
	return jsonOrThrow<AdminUsageReport>(res, 'getTokenUsage');
}

// Per-chat sessions report over [fromMs, toMs). Both epoch ms; fromMs=0 for "all
// time". Admin-only (manager returns 403 for non-admins).
export async function getAdminSessions(fromMs: number, toMs: number): Promise<AdminSessionsReport> {
	const qs = new URLSearchParams({ from: String(Math.trunc(fromMs)), to: String(Math.trunc(toMs)) });
	const res = await fetch(`${API_ORIGIN}/admin/sessions?${qs}`, { headers: await authHeaders() });
	return jsonOrThrow<AdminSessionsReport>(res, 'getAdminSessions');
}

// Per-chat detail: per-turn token/cost breakdown + the MCP byte-estimate table.
// Admin-only.
export async function getAdminSessionDetail(owner: string, id: string): Promise<AdminSessionDetail> {
	const res = await fetch(
		`${API_ORIGIN}/admin/sessions/${encodeURIComponent(owner)}/${encodeURIComponent(id)}`,
		{ headers: await authHeaders() },
	);
	return jsonOrThrow<AdminSessionDetail>(res, 'getAdminSessionDetail');
}

// Admin force-switch: flip another user's EXISTING chat onto a provider/model/effort
// in place (same mechanism as the user-accepted usage-limit fallback). Admin-only.
export async function adminSwitchSession(
	owner: string,
	id: string,
	target: { provider: 'claude' | 'codex'; model?: string; effort?: string },
): Promise<{ ok: boolean; to: 'claude' | 'codex'; model: string; effort: string | null }> {
	const res = await fetch(
		`${API_ORIGIN}/admin/sessions/${encodeURIComponent(owner)}/${encodeURIComponent(id)}/switch`,
		{ method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(target) },
	);
	return jsonOrThrow(res, 'adminSwitchSession');
}

export async function setCapabilityActive(capabilityId: string, active: boolean): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/active`, {
		method: 'PATCH',
		headers: await jsonHeaders(),
		body: JSON.stringify({ active }),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'setCapabilityActive');
	return body.capability;
}

export async function setCapabilityVisibility(
	capabilityId: string,
	visibility: CapabilityVisibility,
): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/visibility`, {
		method: 'PATCH',
		headers: await jsonHeaders(),
		body: JSON.stringify({ visibility }),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'setCapabilityVisibility');
	return body.capability;
}

export async function setCapabilityGrants(capabilityId: string, userIds: string[]): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/grants`, {
		method: 'PUT',
		headers: await jsonHeaders(),
		body: JSON.stringify({ userIds }),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'setCapabilityGrants');
	return body.capability;
}

// ── Admin hub additions (migration 006) ──────────────────────────────────────

// Admin catalog overrides (migration 022): patch editable catalog fields
// (name/summary/description/icon/group/companion_skills). A null value resets
// that field to its seed default. Returns the updated row with `overridden`.
export type CapabilityOverridePatch = Partial<{
	name: string | null;
	summary: string | null;
	description: string | null;
	icon: string | null;
	group: string | null;
	companion_skills: string[] | null;
}>;

export async function setCapabilityOverrides(
	capabilityId: string,
	patch: CapabilityOverridePatch,
): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/overrides`, {
		method: 'PATCH',
		headers: await jsonHeaders(),
		body: JSON.stringify(patch),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'setCapabilityOverrides');
	return body.capability;
}

// Toggle the default-on starter flag (recommended set).
export async function setCapabilityRecommended(
	capabilityId: string,
	recommended: boolean,
): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/recommended`, {
		method: 'PATCH',
		headers: await jsonHeaders(),
		body: JSON.stringify({ recommended }),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'setCapabilityRecommended');
	return body.capability;
}

export async function setCapabilityDefaultInstall(
	capabilityId: string,
	enabled: boolean,
): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/default-install`, {
		method: 'PATCH',
		headers: await jsonHeaders(),
		body: JSON.stringify({ enabled }),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'setCapabilityDefaultInstall');
	return body.capability;
}

// Set/rotate the company-wide shared token (write-only — the secret is never
// returned; the response carries only the updated status). 400 if the cap's auth
// mode has no shared token.
export async function setCapabilityToken(capabilityId: string, token: string): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/token`, {
		method: 'PUT',
		headers: await jsonHeaders(),
		body: JSON.stringify({ token }),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'setCapabilityToken');
	return body.capability;
}

// Clear the company token (reverts to the env-default / drops the server).
export async function clearCapabilityToken(capabilityId: string): Promise<AdminCapability> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${capabilityId}/token`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ capability: AdminCapability }>(res, 'clearCapabilityToken');
	return body.capability;
}

// Grant/revoke admin for a user (migration 006). Revoking an env-floor admin → 400.
export async function setUserAdmin(userId: string, admin: boolean): Promise<AdminUser> {
	const res = await fetch(`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/admin`, {
		method: 'PUT',
		headers: await jsonHeaders(),
		body: JSON.stringify({ admin }),
	});
	const body = await jsonOrThrow<{ user: AdminUser }>(res, 'setUserAdmin');
	return body.user;
}

// Per-user install control. The mutations return the target's fresh install list.
export async function getUserInstalls(userId: string): Promise<AdminUserInstall[]> {
	const res = await fetch(`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/installs`, {
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ installs: AdminUserInstall[] }>(res, 'getUserInstalls');
	return body.installs;
}

export async function addUserInstall(userId: string, capabilityId: string): Promise<AdminUserInstall[]> {
	const res = await fetch(`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/installs`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify({ capabilityId }),
	});
	const body = await jsonOrThrow<{ installs: AdminUserInstall[] }>(res, 'addUserInstall');
	return body.installs;
}

export async function removeUserInstall(userId: string, capabilityId: string): Promise<AdminUserInstall[]> {
	const res = await fetch(
		`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/installs/${encodeURIComponent(capabilityId)}`,
		{ method: 'DELETE', headers: await authHeaders() },
	);
	const body = await jsonOrThrow<{ installs: AdminUserInstall[] }>(res, 'removeUserInstall');
	return body.installs;
}

// Admin reset of a SPECIFIC user's onboarding (per-user analogue of the self-reset).
export async function resetUserOnboarding(userId: string): Promise<AdminUser> {
	const res = await fetch(`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/onboarding/reset`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ user: AdminUser }>(res, 'resetUserOnboarding');
	return body.user;
}

// ── Super admin (migration 029) ─────────────────────────────────────────────
// Every call 403s for a non-super admin (the role is invisible to them, so the
// hub only renders these controls when me().isSuperAdmin).
export async function setUserSuperAdmin(userId: string, superAdmin: boolean): Promise<AdminUser> {
	const res = await fetch(`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/super-admin`, {
		method: 'PUT',
		headers: await jsonHeaders(),
		body: JSON.stringify({ superAdmin }),
	});
	const body = await jsonOrThrow<{ user: AdminUser }>(res, 'setUserSuperAdmin');
	return body.user;
}

export async function listPersonalCapabilities(): Promise<AdminPersonalCapability[]> {
	const res = await fetch(`${API_ORIGIN}/admin/personal-capabilities`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ capabilities: AdminPersonalCapability[] }>(res, 'listPersonalCapabilities');
	return body.capabilities;
}

// Publish a user's personal MCP/skill for everyone (a static-bearer MCP's token
// moves to the encrypted published-token table so it works for all installers).
export async function publishPersonalCapability(capabilityId: string): Promise<AdminPersonalCapability[]> {
	const res = await fetch(`${API_ORIGIN}/admin/personal-capabilities/${encodeURIComponent(capabilityId)}/publish`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ capabilities: AdminPersonalCapability[] }>(res, 'publishPersonalCapability');
	return body.capabilities;
}

export async function removePersonalCapability(capabilityId: string): Promise<AdminPersonalCapability[]> {
	const res = await fetch(`${API_ORIGIN}/admin/personal-capabilities/${encodeURIComponent(capabilityId)}`, {
		method: 'DELETE',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ capabilities: AdminPersonalCapability[] }>(res, 'removePersonalCapability');
	return body.capabilities;
}

export async function listCapabilityInstallers(capabilityId: string): Promise<AdminCapabilityInstaller[]> {
	const res = await fetch(`${API_ORIGIN}/admin/capabilities/${encodeURIComponent(capabilityId)}/installers`, {
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ installers: AdminCapabilityInstaller[] }>(res, 'listCapabilityInstallers');
	return body.installers;
}

// Deactivate / re-activate a capability for ONE user without uninstalling it.
export async function setUserInstallEnabled(
	userId: string,
	capabilityId: string,
	enabled: boolean,
): Promise<AdminUserInstall[]> {
	const res = await fetch(
		`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/installs/${encodeURIComponent(capabilityId)}`,
		{ method: 'PATCH', headers: await jsonHeaders(), body: JSON.stringify({ enabled }) },
	);
	const body = await jsonOrThrow<{ installs: AdminUserInstall[] }>(res, 'setUserInstallEnabled');
	return body.installs;
}

export async function getOffboardPreview(userId: string): Promise<OffboardPreview> {
	const res = await fetch(`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/offboard`, {
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ preview: OffboardPreview }>(res, 'getOffboardPreview');
	return body.preview;
}

export async function offboardUser(
	userId: string,
	decisions: { publishCapabilities: string[]; shareReports: string[] },
): Promise<OffboardResult> {
	const res = await fetch(`${API_ORIGIN}/admin/users/${encodeURIComponent(userId)}/offboard`, {
		method: 'POST',
		headers: await jsonHeaders(),
		body: JSON.stringify(decisions),
	});
	const body = await jsonOrThrow<{ result: OffboardResult }>(res, 'offboardUser');
	return body.result;
}

export async function listOffboarded(): Promise<OffboardedUser[]> {
	const res = await fetch(`${API_ORIGIN}/admin/offboarded`, { headers: await authHeaders() });
	const body = await jsonOrThrow<{ users: OffboardedUser[] }>(res, 'listOffboarded');
	return body.users;
}

export async function reinstateUser(userId: string): Promise<OffboardedUser[]> {
	const res = await fetch(`${API_ORIGIN}/admin/offboarded/${encodeURIComponent(userId)}/reinstate`, {
		method: 'POST',
		headers: await authHeaders(),
	});
	const body = await jsonOrThrow<{ users: OffboardedUser[] }>(res, 'reinstateUser');
	return body.users;
}
